import { apiService, SuperAdminDashboardResponse, KpiMetric } from './api';
import type {
  DateRangeFilter,
  CustomDateRange,
  DashboardOverviewResponse,
  RevenueTrendResponse,
  AppointmentStatusDistributionResponse,
  ServiceAppointmentComparisonResponse,
} from '../types/dashboard.types';

function getDateRange(filter: DateRangeFilter, custom?: CustomDateRange): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  switch (filter) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'last7days':
      start.setDate(start.getDate() - 7);
      break;
    case 'last30days':
      start.setDate(start.getDate() - 30);
      break;
    case 'custom':
      if (custom) {
        return { start: custom.start.toISOString(), end: custom.end.toISOString() };
      }
      break;
    case 'alltime':
    default:
      start.setFullYear(start.getFullYear() - 1);
      break;
  }
  return { start: start.toISOString(), end: end.toISOString() };
}

function findMetric(metrics: KpiMetric[], key: string): { value: number; trend: string; direction: 'up' | 'down' } {
  const m = metrics.find(k => k.key === key);
  if (!m) return { value: 0, trend: '', direction: 'up' };
  const trendText = m.trend?.isAvailable
    ? `${m.trend.percentageChange > 0 ? '+' : ''}${m.trend.percentageChange.toFixed(1)}% vs previous period`
    : 'No trend data';
  return {
    value: m.value,
    trend: trendText,
    direction: (m.trend?.direction === 'down' ? 'down' : 'up') as 'up' | 'down',
  };
}

let cachedResponse: SuperAdminDashboardResponse | null = null;
let cacheKey = '';

async function fetchDashboard(filter: DateRangeFilter, custom?: CustomDateRange): Promise<SuperAdminDashboardResponse> {
  const { start, end } = getDateRange(filter, custom);
  const key = `${start}-${end}`;
  if (cachedResponse && cacheKey === key) return cachedResponse;

  try {
    cachedResponse = await apiService.getSuperAdminDashboard(start, end);
    cacheKey = key;
    return cachedResponse;
  } catch (err) {
    console.error('Failed to fetch super admin dashboard:', err);
    return {};
  }
}

export const dashboardService = {
  async getDashboardOverview(filter: DateRangeFilter, custom?: CustomDateRange): Promise<DashboardOverviewResponse> {
    const data = await fetchDashboard(filter, custom);
    const metrics = data.overview?.metrics || [];

    const totalBookings = findMetric(metrics, 'totalBookings');
    const revenue = findMetric(metrics, 'revenueGmv');
    const activeSalons = findMetric(metrics, 'activeSalons');
    const activeCustomers = findMetric(metrics, 'activeCustomers');
    const cancellationRate = findMetric(metrics, 'cancellationRate');
    const completionRate = findMetric(metrics, 'completionRate');

    return {
      activeCustomers: activeCustomers.value,
      activeCustomersTrend: activeCustomers.trend,
      activeCustomersTrendDirection: activeCustomers.direction,
      activeSalons: activeSalons.value,
      activeSalonsTrend: activeSalons.trend,
      activeSalonsTrendDirection: activeSalons.direction,
      totalBookings: totalBookings.value,
      totalBookingsTrend: totalBookings.trend,
      totalBookingsTrendDirection: totalBookings.direction,
      revenueGmv: revenue.value,
      revenueGmvTrend: revenue.trend,
      revenueGmvTrendDirection: revenue.direction,
      completionRate: completionRate.value,
      completionRateTrend: completionRate.trend,
      completionRateTrendDirection: completionRate.direction,
      cancellationRate: cancellationRate.value,
      cancellationRateTrend: cancellationRate.trend,
      cancellationRateTrendDirection: cancellationRate.direction,
    };
  },

  async getRevenueTrend(filter: DateRangeFilter, custom?: CustomDateRange): Promise<RevenueTrendResponse> {
    const data = await fetchDashboard(filter, custom);
    const revData = data.revenueTrend?.data || [];
    const bookData = data.bookingTrend?.data || [];

    const combined = revData.map((r, i) => ({
      date: r.date,
      label: new Date(r.date).toLocaleDateString('en', { month: 'short', day: 'numeric' }),
      revenue: r.revenue,
      bookings: bookData[i]?.bookings || 0,
    }));

    return { data: combined };
  },

  async getAppointmentStatusDistribution(
    filter: DateRangeFilter,
    custom?: CustomDateRange
  ): Promise<AppointmentStatusDistributionResponse> {
    const data = await fetchDashboard(filter, custom);
    const dist = data.statusDistribution?.data || [];

    const find = (s: string) => dist.find(d => d.status.toLowerCase() === s.toLowerCase())?.value || 0;
    return {
      confirmed: find('Confirmed'),
      pending: find('Pending'),
      canceled: find('Cancelled') || find('Canceled'),
      completed: find('Completed'),
    };
  },

  async getServiceAppointmentComparison(
    filter: DateRangeFilter,
    custom?: CustomDateRange
  ): Promise<ServiceAppointmentComparisonResponse> {
    const data = await fetchDashboard(filter, custom);
    const cats = data.serviceCategoryDistribution?.data || [];

    // Map categories to the existing chart format (serviceA/B/C columns)
    const categoryNames = cats.map(c => c.category);
    const result = [{
      month: 'Current',
      monthLabel: 'Categories',
      serviceA: cats[0]?.value || 0,
      serviceB: cats[1]?.value || 0,
      serviceC: cats[2]?.value || 0,
    }];

    return { data: result, categoryNames };
  },

  // Invalidate cache when filter changes
  invalidateCache() {
    cachedResponse = null;
    cacheKey = '';
  },
};
